import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    insuranceAmountCellRender,
    insuranceDateCellRender,
    isInsuranceRequiredInGrid,
    floodFileHtml,
} from '../common/InsuranceFunctions';
import { InsuranceTypeOptions, InsuranceTableTypeOptions, InsuranceTableType } from '../common/InsuranceConstants';
import { LoanGridType } from '../../common/LoanConstants';
import { BfiDataGrid, Column } from '../../../common/SharedComponents';
import { GridSettingType } from '@apply/constants';
import { borrowerTooltipText, addressTooltipText } from '../../../common/SharedFunctions';
import { InsuranceApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { Tooltip } from 'devextreme-react/tooltip';
import { InsuranceActionsButton } from '../common/InsuranceComponents';
import { onCellDblClick } from '../common/InsuranceFunctions';

const LoanInsuranceTable = ({
    gridType = LoanGridType.MasterNotes,
    tableType = InsuranceTableTypeOptions.BeforeClosingMissing,
    gridStore,
}) => {
    const searchState = useSelector((state) => state.searchState);
    const insuranceApi = new InsuranceApi.InsuranceApi(applicationInsights);
    const location = useLocation();

    const [showAmounts, set_showAmounts] = useState(false);
    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const dataFunction = async (criteria) => {
        criteria.gridType = gridType;
        let data = null;
        //return loanApi.getGrid_StandardLoans(criteria);
        switch (tableType.id) {
            case InsuranceTableType.BeforeClosingMissing:
                data = await insuranceApi.getNotClosedMissing(criteria);
                break;
            case InsuranceTableType.BeforeClosingGood:
                data = await insuranceApi.getNotClosedGood(criteria);
                break;
            case InsuranceTableType.ClosedMissing:
                data = await insuranceApi.getOutstandingExpired(criteria);
                break;
            case InsuranceTableType.ClosedGood:
                data = await insuranceApi.getOutstandingGood(criteria);
                break;
            case InsuranceTableType.Repaid:
                data = await insuranceApi.getRepaid(criteria);
                break;
            default:
                data = [];
                break;
        }

        return data;
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.loanInsurance);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    useEffect(() => {
        set_showAmounts(false);
    }, [tableType, gridType, searchState.searchText.loanInsurance]);

    const setShowAmounts = (show) => {
        if (show === '1') {
            set_showAmounts(true);
        } else {
            set_showAmounts(false);
        }
    };

    const actionCellRender = (e) => {
        return <InsuranceActionsButton data={e.data} />;
    };

    const coverageHeaderRender = (e) => {
        return (
            <div>
                <input
                    type='radio'
                    value='0'
                    //id={'insuranceView0' + id?.toString() + (tableType.id ?? '').toString()}
                    name={`insView_${tableType.id}_${gridType}`}
                    checked={!showAmounts}
                    onChange={(e) => setShowAmounts(e.target.value)}
                />
                <label
                    style={{ fontSize: '12px' }}
                    htmlFor={'insuranceView0' + (tableType ?? '').toString()}
                >
                    Expiry
                </label>
                &nbsp;&nbsp;
                <input
                    type='radio'
                    value='1'
                    //id={'insuranceView1' + id?.toString() + (tableType.id ?? '').toString()}
                    name={`insView_${tableType.id}_${gridType}`}
                    checked={showAmounts}
                    onChange={(e) => setShowAmounts(e.target.value)}
                />
                <label
                    style={{ fontSize: '12px' }}
                    htmlFor={'insuranceView1' + (tableType.id ?? '').toString()}
                >
                    Amount (1000s)
                </label>
            </div>
        );
    };

    const highlightCellRender = (e) => {
        return (
            <span
                style={{ left: '6px' }}
                className={e.data.highlight === 2 ? 'reddot' : e.data.highlight === 1 ? 'yellowdot' : ''}
            ></span>
        );
    };

    const floodCertCellrender = (e) => {
        return floodFileHtml(e.data.floodFileUploaded, e.data.floodUrl);
    };

    const onBorrowerCellRender = (e) => {
        const targetId = `be_${e.data.propertyId}_${gridType}_${tableType.id}`;
        const text = borrowerTooltipText(e.data.borrower);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.borrowerEntity}
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    {text}
                </Tooltip>
            </>
        );
    };

    const onStateCellRender = (e) => {
        const targetId = `as_${e.data.propertyId}_${gridType}_${tableType.id}`;
        const text = addressTooltipText(e.data.address);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.propertyState}
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    {text}
                </Tooltip>
            </>
        );
    };

    let lastPortfolio = {
        id: null,
        className: 'dx_portfolio_highlight1',
    };

    const onRowPrepared = (e) => {
        if (e.rowType !== 'data') {
            return;
        }

        if (e.data.isPortfolio) {
            if (!lastPortfolio.id || lastPortfolio.id === e.data.id) {
                if (!lastPortfolio.id) {
                    lastPortfolio.id = e.data.id;
                }
            } else {
                lastPortfolio.id = e.data.id;
                lastPortfolio.className =
                    lastPortfolio.className === 'dx_portfolio_highlight1' ? 'dx_portfolio_highlight2' : 'dx_portfolio_highlight1';
            }
            const classNames = e.rowElement.className.replace('dx-row-alt', '');
            e.rowElement.className = classNames + ' ' + lastPortfolio.className;
        }
    };

    if (!gridType && !tableType) {
        return '';
    }

    // Calculates the proper GridSettingType using enum typescript magic. Allows individual saved grid settings.
    const gridSettingType = GridSettingType[`LoanInsuranceTable_${LoanGridType[gridType]}_${InsuranceTableType[tableType.id]}`];
    return (
        <BfiDataGrid
            gridType={gridSettingType}
            dataStore={dataStore}
            applicationInsights={applicationInsights}
            onRowPrepared={onRowPrepared}
            onCellDblClick={(e) => onCellDblClick(e, location)}
            wordWrapEnabled={true}
        >
            <Column
                width={20}
                minWidth={20}
                cellRender={highlightCellRender}
            />
            <Column
                dataField='borrowerEntity'
                caption='Entity'
                cellRender={onBorrowerCellRender}
                minWidth={120}
            />
            <Column
                dataField={gridType === LoanGridType.MasterNotes ? 'borrowerState' : 'propertyState'}
                caption={gridType === LoanGridType.MasterNotes ? 'Borr. State' : 'State'}
                cellRender={onStateCellRender}
                width={50}
            />
            <Column
                dataField='bfiNumber'
                caption='BFI #'
                dataType='string'
                width={120}
            />
            <Column
                dataField='loanNumber'
                caption='Loan #'
                dataType='string'
                width={80}
            />
            <Column
                dataField='commitment'
                caption={gridType === LoanGridType.MasterNotes ? 'Line' : 'Approved Amt'}
                dataType='number'
                format='currency'
                width={90}
                alignment={'left'}
            />
            <Column
                dataField='floodFileUploaded'
                caption='CERT'
                width={50}
                alignment={'center'}
                cellRender={floodCertCellrender}
                visible={gridType !== LoanGridType.MasterNotes}
            />
            <Column
                alignment={'center'}
                headerCellRender={coverageHeaderRender}
            >
                {InsuranceTypeOptions.map((it) => {
                    return (
                        <Column
                            key={it.id}
                            caption={it.shortName}
                            dataType='string'
                            width={it.abbr === 'GL' ? 100 : 90}
                            alignment='center'
                            cellRender={(e) => {
                                return showAmounts ? insuranceAmountCellRender(it, e.data) : insuranceDateCellRender(it, e.data);
                            }}
                            visible={isInsuranceRequiredInGrid(gridType, it.id)}
                        />
                    );
                })}
            </Column>
            <Column
                dataField='pointOfContact'
                caption='POC'
                dataType='string'
                width={120}
            />
            <Column
                dataField='ownerName'
                caption='User'
                dataType='string'
                width={120}
            />
            <Column
                dataField='broker'
                dataType='string'
                width={120}
            />
            <Column
                alignment='center'
                cellRender={actionCellRender}
                width={50}
                allowReordering={false}
                allowSorting={false}
                allowResizing={false}
                type='actions'
                fixed={true}
                fixedPosition={'right'}
            />
        </BfiDataGrid>
    );
};

export default LoanInsuranceTable;
